import React from 'react';
import { fmtCount, fmtSharePct } from '../lib/format';
import { SERIES } from '../lib/colors';

/**
 * Evidence for `discovery_breadth`.
 *
 * `facts`: { anonymized_click_share, named_query_clicks, anonymized_clicks,
 *            named_queries, total_clicks }
 *
 * F7 is the whole point of this component. `anonymized_clicks` is REACH, not missing
 * data: Google withholds the search term when a query is rare enough that publishing it
 * could identify the searcher. A visit is still a visit and it is still counted. So the
 * two segments are labelled exactly "searches we can name" and "very specific searches",
 * and the words missing / lost / hidden / untracked / unknown / gap never appear —
 * `ShareTrend.test.js` asserts that directly.
 *
 * The share bar is drawn from the two pre-computed click counts as widths; no
 * percentage is derived for display. The headline percentage is
 * `facts.anonymized_click_share` exactly as returned (F2).
 */
/** A count present in the payload. `Number(null)` is 0, so null must be rejected first. */
const count = (v) => (v === null || v === undefined || v === '' || !Number.isFinite(Number(v)) ? null : Number(v));

const ShareTrend = ({ facts }) => {
  const named = count(facts?.named_query_clicks);
  const anonymized = count(facts?.anonymized_clicks);
  const bothPresent = named !== null && anonymized !== null && named + anonymized > 0;
  const share = fmtSharePct(facts?.anonymized_click_share);

  return (
    <div className="space-y-4">
      {share && (
        <div>
          <p className="text-3xl font-bold text-gray-900 dark:text-gray-100 tabular-nums">{share}</p>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            of visits came from very specific searches
          </p>
        </div>
      )}

      {bothPresent && (
        <div>
          {/* Widths are a layout proportion, not a displayed number — every figure
              printed below is a value straight from `facts`. */}
          <div className="flex h-3 w-full overflow-hidden rounded-full" aria-hidden="true">
            <div style={{ width: `${(named / (named + anonymized)) * 100}%`, backgroundColor: SERIES.clicks }} />
            <div style={{ width: `${(anonymized / (named + anonymized)) * 100}%`, backgroundColor: SERIES.brand }} />
          </div>
          {/* A flex-wrap row, not a viewport-breakpoint grid: this panel sits in a narrow
              tier slot, so `sm:grid-cols-2` forced two 1fr tracks the panel could not
              afford, and with the labels held on one line (`whitespace-nowrap`) the tracks
              could not shrink — their content overflowed and overlapped. Flex items wrap as
              whole units instead: side by side when there is room, stacked when there is
              not, and never overlapping. */}
          <dl className="mt-3 flex flex-wrap gap-x-6 gap-y-2 text-sm">
            <div className="flex items-baseline gap-2">
              <span aria-hidden="true" className="mt-1 h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: SERIES.clicks }} />
              <dt className="text-gray-500 dark:text-gray-400 whitespace-nowrap">Searches we can name</dt>
              <dd className="font-semibold text-gray-900 dark:text-gray-100 tabular-nums">{fmtCount(named)}</dd>
            </div>
            <div className="flex items-baseline gap-2">
              <span aria-hidden="true" className="mt-1 h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: SERIES.brand }} />
              <dt className="text-gray-500 dark:text-gray-400 whitespace-nowrap">Very specific searches</dt>
              <dd className="font-semibold text-gray-900 dark:text-gray-100 tabular-nums">{fmtCount(anonymized)}</dd>
            </div>
          </dl>
          <p className="mt-2 text-xs text-gray-400 dark:text-gray-500">
            Google keeps the wording of a very specific search private, so those visits
            are counted here without their search terms.
          </p>
        </div>
      )}
    </div>
  );
};

export default ShareTrend;
